export default function FortLauderdaleDestinationsSection() {
  const stats = [
    {
      value: "58%",
      label: "of Fort Lauderdale requests are Caribbean cruise or resort departures with under 3 weeks notice",
    },
    {
      value: "1–2 weeks",
      label: "average notice for yacht charter and marine industry trips from Fort Lauderdale",
    },
    {
      value: "Jan–Apr",
      label: "peak travel season — passport renewals spike before spring break and yacht show season",
    },
    {
      value: "24-hour",
      label: "shortest emergency departure window we've accommodated for a Fort Lauderdale yacht crew member",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for Fort Lauderdale travelers — by cruise from Port Everglades, by yacht across the Gulf Stream, or by direct flight from FLL to Nassau, San Juan, or Montego Bay. The proximity of both the airport and cruise port makes Caribbean travel an impulse decision. All cruise passengers and most air travelers need valid passports with at least six months remaining validity.",
      urgency: "Cruise and yacht charter embarkation deadlines are absolute — passport issues discovered days before departure require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Cozumel are staple destinations for Fort Lauderdale families and groups. Direct and connecting service from FLL to Mexico City, Guadalajara, and Cancun serves both leisure and business travelers. Mexico requires six months of passport validity for entry, and the spring break booking surge from January through March creates compressed timelines.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Colombia & South America",
      context: "Bogota, Medellin, Cali, and Cartagena draw Fort Lauderdale's Colombian community for family visits and business. FLL has expanded direct South American service, making these destinations increasingly accessible without connecting through Miami. Each country requires six months of passport validity, and family emergencies can require travel within 48–72 hours.",
      urgency: "Family emergencies in Colombia can require travel within 48–72 hours — standard processing is not an option",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe & Mediterranean",
      context: "Paris, London, Rome, Barcelona, and the South of France attract Fort Lauderdale yachting professionals for boat shows, client meetings, and charter seasons. The European yachting season (April–October) creates concentrated travel demand. The Schengen Area requires three months of passport validity beyond your planned departure date.",
      urgency: "European yacht show season bookings happen in February–March — passport validity checks should happen then",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto and Montreal draw Fort Lauderdale business travelers for finance, healthcare, and technology conferences. Canadian ski destinations attract winter sports enthusiasts. Canada requires a valid passport for all air arrivals, and many Fort Lauderdale families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Business conference registration deadlines often require confirmed travel 4–6 weeks in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Cuba & Dominican Republic",
      context: "Heritage travel and family visits to Cuba and the Dominican Republic are central to many Fort Lauderdale households. Whether visiting relatives in Havana, Santiago, Santo Domingo, or Puerto Plata, these trips carry emotional weight and urgency. The Cuban and Dominican communities in Fort Lauderdale create some of the most time-sensitive passport situations.",
      urgency: "Family visit travel is often booked with 1–2 weeks of notice — expired passports require immediate expedited processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Fort Lauderdale Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Fort Lauderdale
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Fort Lauderdale travelers head to the Caribbean by cruise and yacht, Mexico, Latin America, Europe, and Canada for leisure cruises, yacht charters, family visits, and international business. These are the destinations that create the most passport urgency for our Fort Lauderdale clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Hollywood", href: "/cities/hollywood/" },
              { name: "Miami", href: "/cities/miami/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "Aventura", href: "/cities/aventura/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
              { name: "Plantation", href: "/cities/plantation/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}